/**
 * SYSTEM SETTINGS MODULE
 * Per-tenant integration config. Reads/writes the account-scoped
 * `integration_settings` table over RLS and invokes the top-level edge
 * functions (`google-oauth-exchange`, `sheets-sync`) — cross-module data flows
 * through those documented service boundaries, never a direct module import.
 * The OAuth refresh token lives in a service-role-only table and is never read
 * here.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";
import { getMyPrimaryAccount } from "../../../shared/utils/accountService.js";

const PROVIDER = "google_sheets";
/**
 * ⚠️ NO EXPIRY WINDOW HERE, AND THERE IS NOTHING TO ADD BACK.
 * `integration_settings.recency_months` existed for about a day (migration 0086)
 * and is dropped by 0096. The inventory sync no longer keeps rows on a timer
 * after a state leaves the sheet: the sheet is the source of truth, so a state it
 * stops listing comes off on the next sync that resolves the whole file cleanly.
 * There is no window left to configure, so there is no control and no saveable
 * column. (`retail_data_source.recency_months` is a DIFFERENT column on a
 * different table, owned by the Retail Product Finder panel — leave it alone.)
 */
const SAVEABLE = [
  "spreadsheet_id", "spreadsheet_name", "sheet_title", "header_row",
  "sync_enabled", "sync_cadence", "connected_email",
];

let _acct = null;
async function acctId() {
  if (_acct) return _acct;
  const a = await getMyPrimaryAccount();
  if (!a?.accountId) throw new Error("No workspace found for your account.");
  _acct = a.accountId;
  return _acct;
}

/** Config row for a dataset ('depletions' | 'inventory' | 'retail_locations'), or null. */
export async function getIntegration(dataset) {
  const account = await acctId();
  const { data, error } = await supabase
    .from("integration_settings")
    .select("*")
    .eq("account_id", account).eq("provider", PROVIDER).eq("dataset", dataset)
    .maybeSingle();
  if (error) { console.error("[integrationService.getIntegration]", error); throw new Error("Could not load the integration."); }
  return data;
}

/** Upsert the config row (whitelisted columns; account_id injected). */
export async function saveIntegration(dataset, patch = {}) {
  const account = await acctId();
  const row = { account_id: account, provider: PROVIDER, dataset };
  for (const k of SAVEABLE) if (patch[k] !== undefined) row[k] = patch[k];
  const { data, error } = await supabase
    .from("integration_settings")
    .upsert(row, { onConflict: "account_id,provider,dataset" })
    .select("*")
    .single();
  if (error) { console.error("[integrationService.saveIntegration]", error); throw new Error("Could not save the integration."); }
  return data;
}

// ⚠️ These edge functions run with the service role and cannot see the workspace
// switcher, so each call names the workspace explicitly. Left to guess, a Google
// refresh token obtained while switched to one workspace was stored against
// another — and every later sync then wrote that sheet's rows into the wrong
// tenant. The function re-verifies membership of whatever id we send.

/** Exchange a Google authorization code (from GIS) for a stored refresh token. */
export async function exchangeGoogleCode(code) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("google-oauth-exchange", { body: { code, accountId } });
  if (error) throw new Error(error.message || "Google connection failed.");
  if (!data?.ok) throw new Error(data?.error || "Google connection failed.");
  return data; // { ok, email }
}

/** Create a pre-formatted starter Google Sheet in the user's Drive + connect it. */
export async function createTemplateSheet(dataset) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("sheets-sync", { body: { action: "create_template", dataset, accountId } });
  if (error) throw new Error(error.message || "Could not create the sheet.");
  if (!data?.ok) throw new Error(data?.error || "Could not create the sheet.");
  return data; // { spreadsheetId, name, url }
}

/**
 * Run a sync now for one dataset. Returns the whole `sheets-sync` response so
 * callers can report on it without waiting for the next read of
 * `last_sync_message`:
 *   { ok, rows, skipped, dimMissRows, dimUnmatched, productNote,
 *     unmatchedProducts, unmatchedPairs, unmatchedPairsDistinct }
 * plus the row-removal counts the run performed — `removedRows` /
 * `removedStates` / `removedOrphans` (inventory: rows deleted because the sheet
 * stopped listing those states, migration 0096), `absentStates` + `keptForSkips`
 * (inventory: there WAS something to remove and the run kept it, because rows
 * the sheet could not resolve make "missing" indistinguishable from "not
 * recognised"), and `deactivated` (retail: stores hidden) — and `skipReason`,
 * the wording the run actually skipped for. The panel renders those
 * defensively, so it stays correct whether or not a given deployment of the
 * function sends them.
 */
export async function syncNow(dataset) {
  const accountId = await acctId();
  const { data, error } = await supabase.functions.invoke("sheets-sync", { body: { dataset, accountId } });
  if (error) throw new Error(error.message || "Sync failed.");
  if (!data?.ok) throw new Error(data?.error || "Sync failed.");
  return data;
}
